/** Pure shape-detection for LLM output — no React, so any surface
 *  (and its tests) can use these without rendering. */

/** The JSON array-of-objects in ``text`` (code fence stripped), or null. */
export function tryExtractTableRows(text: string | null): Record<string, unknown>[] | null {
  if (!text) return null;
  const stripped = text
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  // Only when the whole payload IS the array — slicing it out of prose drops the prose.
  if (!stripped.startsWith("[") || !stripped.endsWith("]")) return null;
  const candidate = stripped;
  try {
    const parsed = JSON.parse(candidate);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (!parsed.every((row) => row && typeof row === "object" && !Array.isArray(row))) return null;
    return parsed as Record<string, unknown>[];
  } catch {
    return null;
  }
}

/** A JSON object whose values are all markdown strings — the usual shape of
 *  schema-having prompts — split into (key, markdown) blocks. */
export function tryExtractMarkdownSections(text: string | null): [string, string][] | null {
  if (!text) return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith("{")) return null;
  try {
    const parsed = JSON.parse(trimmed);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return null;
    const entries = Object.entries(parsed);
    if (entries.length === 0 || !entries.every(([, v]) => typeof v === "string")) return null;
    return entries as [string, string][];
  } catch {
    return null;
  }
}

const sortKeys = (row: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(row).sort(([a], [b]) => a.localeCompare(b)));

/** Comparison form of the output: structured output re-serialized
 *  deterministically, so JSON formatting, key order, and escapes stop
 *  counting as differences. */
/** The output as plain comparable text, for fuzzy scoring (similarity,
 *  diff gating): section/table content with the JSON envelope stripped, so
 *  punctuation and \n escapes don't glue words together and drag the score
 *  down. Not collision-free — identity checks use canonicalizeLlmOutput. */
export function comparableLlmText(text: string | null): string {
  if (!text) return "";
  const structured = tryParseStructured(text);
  if (structured !== null) return structuredToComparableText(structured);
  const rows = tryExtractTableRows(text);
  if (rows) {
    return rows
      .map((row) =>
        Object.entries(sortKeys(row))
          .map(([key, value]) => `${key} ${typeof value === "string" ? value : JSON.stringify(value)}`)
          .join(" "),
      )
      .join("\n");
  }
  const sections = tryExtractMarkdownSections(text);
  if (sections) {
    return [...sections]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => `${key}\n${value}`)
      .join("\n\n");
  }
  return text;
}

export function canonicalizeLlmOutput(text: string | null): string {
  if (!text) return "";
  const structured = tryParseStructured(text);
  if (structured !== null) return JSON.stringify(sortDeep(structured));
  const rows = tryExtractTableRows(text);
  if (rows) return JSON.stringify(rows.map(sortKeys));
  const sections = tryExtractMarkdownSections(text);
  if (sections) {
    // JSON, not newline joins: a value containing the next key as a
    // paragraph must not collide with a genuinely different object.
    return JSON.stringify([...sections].sort(([a], [b]) => a.localeCompare(b)));
  }
  return text;
}

/** Full-payload JSON of any shape (object or array), fence-stripped —
 *  null when the text isn't wholly structured. */
export function tryParseStructured(text: string | null): unknown | null {
  if (!text) return null;
  const stripped = text
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  const wrapped =
    (stripped.startsWith("{") && stripped.endsWith("}")) || (stripped.startsWith("[") && stripped.endsWith("]"));
  if (!wrapped) return null;
  try {
    const value: unknown = JSON.parse(stripped);
    return value && typeof value === "object" ? value : null;
  } catch {
    return null;
  }
}

const sortDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortDeep);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([k, v]) => [k, sortDeep(v)]),
    );
  }
  return value;
};

/** Readable text for a structured value: keys and content, envelope gone —
 *  what similarity scoring runs on for nested shapes. */
export function structuredToComparableText(value: unknown): string {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.map(structuredToComparableText).join("\n");
  if (value && typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k} ${structuredToComparableText(v)}`)
      .join("\n");
  }
  return String(value ?? "");
}
